import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Tasks from "@/pages/Tasks";
import TaskDetail from "@/pages/TaskDetail";
import SerpAnalysis from "@/pages/SerpAnalysis";
import KeywordSearch from "@/pages/KeywordSearch";
import BatchAnalysis from "@/pages/BatchAnalysis";
import ProjectExport from "@/pages/ProjectExport";
import DataStorage from "@/pages/DataStorage";
import Projects from "@/pages/Projects";
import Logs from "@/pages/Logs";
import UserLogs from "@/pages/UserLogs";
import Admin from "@/pages/Admin";
import SerpSettings from "@/pages/SerpSettings";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import DataViewer from "@/pages/DataViewer";
import KeywordGroups from "@/pages/KeywordGroups";
import KeywordGroupDetail from "@/pages/KeywordGroupDetail";
import BatchScraper from "@/pages/BatchScraper";
import AlibabaSearch from "@/pages/AlibabaSearch";
import InsightReport from "@/pages/InsightReport";
import InsightsSummary from "@/pages/InsightsSummary";
import CrawlStrategyManager from "@/pages/CrawlStrategyManager";
import ImageBlocklist from "@/pages/ImageBlocklist";
import PipelineMonitor from "@/pages/PipelineMonitor";
import AnalysisReports from "@/pages/AnalysisReports";
import AnalysisReportDetail from "@/pages/AnalysisReportDetail";
import ShareMyImageAdmin from "@/pages/ShareMyImageAdmin";
import NavHealth from "@/pages/NavHealth";
import PromptManager from "@/pages/PromptManager";
import ProductProfile from "@/pages/ProductProfile";
import NotFound from "@/pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/tasks/:id" element={<TaskDetail />} />
              <Route path="/serp" element={<SerpAnalysis />} />
              <Route path="/serp-analysis" element={<SerpAnalysis />} />
              <Route path="/keyword-search" element={<KeywordSearch />} />
              <Route path="/batch-analysis" element={<BatchAnalysis />} />
              <Route path="/project-export" element={<ProjectExport />} />
              <Route path="/data-storage" element={<DataStorage />} />
              <Route path="/data-viewer" element={<DataViewer />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/keyword-groups" element={<KeywordGroups />} />
              <Route path="/keyword-groups/:groupId" element={<KeywordGroupDetail />} />
              <Route path="/batch-scraper" element={<BatchScraper />} />
              <Route path="/alibaba-search" element={<AlibabaSearch />} />
              <Route path="/alibaba-insights" element={<InsightsSummary />} />
              <Route path="/alibaba-insights/:tag" element={<InsightReport />} />
              <Route path="/crawl-strategies" element={<CrawlStrategyManager />} />
              <Route path="/image-blocklist" element={<ImageBlocklist />} />
              <Route path="/pipeline-monitor" element={<PipelineMonitor />} />
              <Route path="/nav-health" element={<NavHealth />} />
              <Route path="/analysis-reports" element={<AnalysisReports />} />
              <Route path="/analysis-reports/:requestId" element={<AnalysisReportDetail />} />
              <Route path="/prompt-manager" element={<PromptManager />} />
              <Route path="/logs" element={<Logs />} />
              <Route path="/user-logs" element={<UserLogs />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/serp-settings" element={<SerpSettings />} />
              <Route path="/admin/sharemyimage" element={<ShareMyImageAdmin />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/product/:task_id" element={<ProductProfile />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
